import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CentsToDollarsPipe } from '@marmon/frontend/common';
import {
	Analysis,
	applyPhase1Quads,
	applyPhase2Quads,
	PeriodProfitModelType,
	ProfitabilityGrid,
	ProfitabilityModel,
	QuadPeriodProfit,
} from '@marmon/shared/types/analysis';
import { currencyTextSymbol } from '@marmon/shared/types/common';
import { TuiSkeleton } from '@taiga-ui/kit';
import { IsCostTypePipe } from '../../pipes';

@Component({
	standalone: true,
	imports: [
		TranslocoPipe,
		CentsToDollarsPipe,
		NgTemplateOutlet,
		TuiSkeleton,
		IsCostTypePipe,
	],
	selector: 'mar-profitability-model',
	templateUrl: 'profitability-model.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'flex flex-col gap-6 items-center w-full' },
})
export class ProfitabilityModelComponent {
	readonly periods = input.required<QuadPeriodProfit[] | null>();
	readonly analysis = input.required<Analysis>();
	readonly modelType = input<PeriodProfitModelType>('overview');

	/**
	 * the count of columns to show
	 */
	readonly columns = input<number>(4);

	readonly loading = input<boolean>(true);

	readonly currencySymbol = computed(() =>
		currencyTextSymbol(this.analysis().currency),
	);

	readonly profitabilityModel = computed((): ProfitabilityGrid => {
		const periods = this.periods();
		if (!periods) return new ProfitabilityModel().valueOf();

		const type = this.modelType();
		if (type === 'overview') {
			return this.generateOverviewModel(periods);
		} else if (type === 'phases') {
			return this.generatePhasesModel(periods);
		}

		throw Error(`Model type not supported: ${type}`);
	});

	generateOverviewModel(periods: QuadPeriodProfit[]) {
		const periodsAfter = applyPhase2Quads(periods, this.analysis());

		const quadTotal = periods.find((p) => p.key === 'quadTotal');
		const quadTotalAfter = periodsAfter.find((p) => p.key === 'quadTotal');

		if (!quadTotal || !quadTotalAfter) return [];

		return new ProfitabilityModel({
			type: 'overview',
			columns: [quadTotal.total, quadTotalAfter.total],
		}).valueOf();
	}

	generatePhasesModel(periods: QuadPeriodProfit[]) {
		const periodsPhase1 = applyPhase1Quads(periods, this.analysis());
		const periodsPhase2 = applyPhase2Quads(periods, this.analysis());

		const quadTotal = periods.find((p) => p.key === 'quadTotal');
		const phase1Total = periodsPhase1.find((p) => p.key === 'quadTotal');
		const phase2Total = periodsPhase2.find((p) => p.key === 'quadTotal');
		if (!quadTotal || !phase1Total || !phase2Total) return [];

		return new ProfitabilityModel({
			type: 'phases',
			columns: [quadTotal.total, phase1Total.total, phase2Total.total],
		}).valueOf();
	}
}
